import Select from '@mui/material/Select'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import React from 'react'
import FlexBox from '../FlexBox'

export const StyledContainer = ({
  className,
  ...rest
}: React.ComponentProps<'div'>) => (
  // Desktop: bound to viewport so the left rail can be its own scroll
  // container (and the map's sticky/fixed-height column has a real
  // boundary). Same calc as `MapContainer.md:h-[...]` — both fill the
  // space between the filters bar and the viewport bottom. With this,
  // scroll-to-top on filter / map-pan can target the rail itself, not
  // the window, so the page chrome (nav + filters + below-fold
  // seoFooter) doesn't shift under the user.
  // Mobile: stays `h-auto` (drawer owns its own scroll).
  <div
    className={`grid grid-cols-1 w-full bg-grey-250 md:bg-white-500 md:grid-cols-[45%_55%] md:h-[calc(100vh-var(--nav-h)-38px-16px)] md:mt-[calc(var(--nav-h)+38px+16px)] ${className ?? ''}`.trim()}
    {...rest}
  />
)

export const StyledButtonContainer = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`fixed bottom-0 flex justify-center w-full p-[16px] z-[100] pointer-events-none md:hidden ${className ?? ''}`.trim()}
    {...rest}
  />
)

export const StyledSwipeableDrawer = ({
  className,
  slotProps,
  ...rest
}: React.ComponentProps<typeof SwipeableDrawer>) => (
  <SwipeableDrawer
    className={`md:hidden ${className ?? ''}`.trim()}
    slotProps={{
      ...slotProps,
      // No mount ("appear") transition, on the paper OR the backdrop. Since
      // 2026-10-28 SearchManager renders this drawer inline (`disablePortal`),
      // so it is in the server HTML and hydrates already open — an appear
      // transition there would replay the slide over a sheet the reader can
      // already see. Before that it mounted client-side, and Drawer only skips
      // the appear Slide through a ref that flips in an effect — the Portal
      // commits the Slide one commit later, so the slide-up ran anyway. Each
      // transition's `reflow()` (`node.scrollTop`) forces a full layout of a
      // ~3.7k-node page right after hydration: two of them, ~240 ms at 4× CPU
      // on the city page (2026-09-25 trace), inside the first-tap window.
      // Nothing visible is lost — the content is not on screen during that
      // slide, and the backdrop is forced to opacity 0 below. Later swipes
      // open/close still animate; `appear` only covers the mount. A
      // `transitionDuration: 0` would NOT have helped: Fade and Slide call
      // `reflow()` on enter regardless of duration.
      transition: {
        appear: false,
        ...(slotProps?.transition as object | undefined)
      },
      paper: {
        ...slotProps?.paper,
        // The swipe handle is pinned `top: -75px` above the paper, so the
        // paper's top edge minus 75 is where the handle starts. Subtracting
        // an extra ~20px below the nav lets the map peek through before the
        // swipe handle and signals the map is still there behind the drawer.
        className:
          `h-[calc(100%-var(--nav-h)-38px-16px-40px)] overflow-visible bg-grey-250 border-0 shadow-none ${
            (slotProps?.paper as { className?: string } | undefined)
              ?.className ?? ''
          }`.trim()
      },
      backdrop: {
        ...slotProps?.backdrop,
        className: `opacity-0! ${
          (slotProps?.backdrop as { className?: string } | undefined)
            ?.className ?? ''
        }`.trim(),
        slotProps: {
          ...(slotProps?.backdrop as { slotProps?: object } | undefined)
            ?.slotProps,
          transition: { appear: false }
        }
      }
    }}
    {...rest}
  />
)

export const StyledSelect = ({
  className,
  ...rest
}: React.ComponentProps<typeof Select>) => (
  <Select
    className={`text-[#465B75] text-[14px] [&_.MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall]:pl-[5px] [&_.MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall]:pr-[5px] [&_.MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall]:font-medium [&_.MuiOutlinedInput-notchedOutline]:border-0 [&_.MuiSelect-select\\[aria-expanded=true\\]~.icon_i]:rotate-180 ${className ?? ''}`.trim()}
    {...rest}
  />
)

export const MapContainer = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    // Mobile: anchor with explicit `top` + `100dvh`. The previous combo
    // `max-md:fixed max-md:mt-(--nav-h) max-md:h-[calc(100%-var(--nav-h))]`
    // relied on the static-position fallback for fixed elements and
    // resolved `100%` against the initial containing block, which on
    // iOS Safari shifts as the URL bar slides — causing a visible
    // "white gap appears, map drops down" flicker on each search.
    // Loading-state skeleton bg lives on the empty-state overlay in
    // SearchManager (only paints while `emptyAndLoading`); the container
    // itself stays transparent so once Google Maps mounts the tiles fill
    // edge-to-edge with no skeleton color bleeding through at the seams.
    className={`relative max-md:w-full max-md:fixed max-md:top-(--nav-h) max-md:h-[calc(100dvh-var(--nav-h))] md:overflow-hidden md:block md:top-[calc(var(--nav-h)+38px+16px)] md:h-[calc(100vh-var(--nav-h)-38px-16px)] md:sticky ${className ?? ''}`.trim()}
    {...rest}
  />
)

interface SearchListContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  show?: number
}

export const SearchListContainer = ({
  show,
  className,
  style,
  ...rest
}: SearchListContainerProps) => (
  <div
    className={`relative overflow-y-scroll h-full max-md:pb-[70px] md:p-[10px] md:block ${className ?? ''}`.trim()}
    style={{ display: show ? 'block' : 'none', ...style }}
    {...rest}
  />
)

export const StyledSectionTitle = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={`text-[#465B75] text-[24px] font-bold leading-[26px] text-center my-[15px] md:text-[30px] md:leading-[42px] ${className ?? ''}`.trim()}
    {...rest}
  />
)

export const StyledModalButtonContainer = ({
  className,
  ...rest
}: React.ComponentProps<typeof FlexBox>) => (
  <FlexBox className={`max-md:flex-col ${className ?? ''}`.trim()} {...rest} />
)

export const StyledSectionSubtitle = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={`text-[#465B75] text-[14px] leading-[17px] text-center m-0 md:text-[16px] md:leading-[19px] ${className ?? ''}`.trim()}
    {...rest}
  />
)
